<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import * as yup from 'yup';

  import { FormGroup, InlineNotification } from 'carbon-components-svelte';
  import { Form } from '@mst-fe/sveltejs-forms';

  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import VenueFormItems from './VenueFormItems.svelte';

  import { addVenue, updateVenue, type MarketVenueFormValues, type MarketVenueRow } from '../../../services';
  import { getServerErrorMessage } from '../../../utils';

  export let formSubmitButtonRef: HTMLButtonElement,
    loading: boolean = false,
    initialValues: {
      venueId?: string;
    } & Partial<MarketVenueFormValues>;

  const dispatch = createEventDispatcher<{ close: { action: 'edit-venue' | 'add-venue'; data: MarketVenueRow } }>();
  const schema = yup.object().shape({
    countryCode: yup.string().required('Country code is required.').length(2, 'Country code must be exactly 2 characters.').uppercase(),
    exchangeName: yup.string().required('Exchange is required.'),
    mic: yup.string().required('MIC is required.').length(4, 'MIC must be exactly 4 characters.').uppercase(),
    timezone: yup
      .string()
      .required('Timezone is required.')
      .test('valid-timezone', 'Select a valid timezone', (value?: string) =>
        value ? Intl.supportedValuesOf('timeZone').includes(value) : false
      ),
  });

  let resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null;

  async function handleFormSubmission({ detail: { values } }: { detail: { values: Partial<MarketVenueFormValues> } }) {
    resultNotification = null;
    loading = true;

    try {
      const savedVenue = isEditMode
        ? await updateVenue(initialValues.venueId as string, values)
        : await addVenue(values as MarketVenueFormValues);

      dispatch('close', { action: isEditMode ? 'edit-venue' : 'add-venue', data: savedVenue });
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Verify your submission and try again.';
      console.error(`[UpdateVenueForm] Failed to submit!`, error);

      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to submit! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  $: isEditMode = Boolean(initialValues.venueId);
</script>

{#if resultNotification}
  <InlineNotification
    kind={resultNotification.kind}
    lowContrast
    title={resultNotification.title}
    subtitle={resultNotification.subtitle}
    hideCloseButton
  />
{/if}
{#if isEditMode}
  <InlineNotification
    kind="info"
    lowContrast
    subtitle="Changes under venue details will be inherited by all connected feeds."
    hideCloseButton
  />
{/if}
<LoadingSpinner {loading}>
  <Form
    fieldDomString="input,select,textarea,div[name]"
    {initialValues}
    {schema}
    let:submitForm
    let:errors
    let:setValue
    let:touched
    let:values
    on:submit={handleFormSubmission}
  >
    <FormGroup>
      <VenueFormItems {errors} {touched} {values} {setValue} />
      <button hidden type="button" on:click={submitForm} bind:this={formSubmitButtonRef} />
    </FormGroup>
  </Form>
</LoadingSpinner>
